import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { OrgType, OrgContextValue } from '../types/context';

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export const useOrg = (): OrgContextValue => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

interface OrgProviderProps {
  children: ReactNode;
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const [org, setOrgState] = useState<OrgType>(null);
  const [isHydrating, setIsHydrating] = useState(true);

  // Load org from localStorage on mount
  useEffect(() => {
    const storedOrg = localStorage.getItem('barebiologics_org');
    if (storedOrg && (storedOrg === 'bare' || storedOrg === 'epl')) {
      setOrgState(storedOrg);
    }
    setIsHydrating(false);
  }, []);

  const setOrg = (newOrg: OrgType) => {
    setOrgState(newOrg);
    if (newOrg) {
      localStorage.setItem('barebiologics_org', newOrg);
    } else {
      localStorage.removeItem('barebiologics_org');
    }
  };

  const clearOrg = () => {
    setOrgState(null);
    localStorage.removeItem('barebiologics_org');
  };

  const value: OrgContextValue = {
    org,
    setOrg,
    clearOrg,
    isHydrating
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export default OrgContext;
